// client/src/pages/business/ProductDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Package } from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FavoriteButton from '../../components/favorites/FavoriteButton';
import { productsAPI, productDiscountsAPI, flashOffersAPI, catalogDiscountsAPI, discountsAPI, businessAPI } from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { Tag } from 'lucide-react';
// ✅ FIX: Import image helper
import { getImageUrl } from '../../utils/imageHelper';
import useCategoryLabels from '../../hooks/useCategoryLabels';

const ProductDetails = () => {
  const { t } = useTranslation(['business', 'products']);
  const { getCategoryLabel } = useCategoryLabels();
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isAuthenticated, user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(null);
  const [hidePricing, setHidePricing] = useState(false);
  const [productDiscount, setProductDiscount] = useState(null); // { percentage }
  const [customerDiscountPct, setCustomerDiscountPct] = useState(0);
  const [catalogDiscountsForWholesaler, setCatalogDiscountsForWholesaler] = useState([]);
  const [flashOffer, setFlashOffer] = useState(null); // { offerPrice, offerId, offerTitle }

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await productsAPI.getById(id);
      const prod = response.data.data || response.data;
      setProduct(prod);
      setQuantity(prod.pricing?.minOrderQuantity || 1);

      const wId = prod.wholesalerId
        ? (typeof prod.wholesalerId === 'object' ? prod.wholesalerId._id : prod.wholesalerId)
        : null;

      // Check if wholesaler hides pricing
      if (wId) {
        try {
          const wRes = await import('../../services/api').then(m => m.wholesalersAPI.getCatalog(wId, { limit: 1 }));
          if (wRes.data.hidePricing) setHidePricing(true);
        } catch { /* ignore — pricing visible by default */ }
      }

      // Only fetch discounts when the viewer is a business user
      if (wId && isAuthenticated && user?.role === 'business') {
        const widStr = String(wId);
        const pidStr = String(prod._id);

        // Product-level discounts for this wholesaler
        try {
          const res = await productDiscountsAPI.getMyDiscounts(widStr);
          if (res.data.success && res.data.discounts && typeof res.data.discounts === 'object') {
            const match = res.data.discounts[pidStr];
            if (match) setProductDiscount(match);
          }
        } catch { /* silent */ }

        // Customer discount
        try {
          const profileRes = await businessAPI.getProfile();
          const businessId = profileRes.data.business?._id;
          if (businessId) {
            const res = await discountsAPI.getCustomerDiscountsForBusiness(businessId, widStr);
            if (res.data.success && res.data.hasDiscounts) {
              const totalPct = res.data.discounts.reduce((sum, d) => sum + (d.percentage || 0), 0);
              setCustomerDiscountPct(Math.min(totalPct, 100));
            }
          }
        } catch { /* silent */ }

        // Catalog discounts
        try {
          const res = await catalogDiscountsAPI.getForWholesaler(widStr);
          if (res.data.discounts?.length > 0) setCatalogDiscountsForWholesaler(res.data.discounts);
        } catch { /* silent */ }

        // Flash offers
        try {
          const foRes = await flashOffersAPI.getAvailable();
          (foRes.data.offers || []).some(offer =>
            (offer.items || []).some(item => {
              if (item.productId && String(item.productId) === pidStr && item.offerPrice != null) {
                setFlashOffer({
                  offerPrice: item.offerPrice,
                  offerId: offer._id,
                  offerTitle: offer.title
                });
                return true;
              }
              return false;
            })
          );
        } catch { /* silent */ }
      }
    } catch (err) {
      setError(t('productDetails.error'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Compute effective price (mirrors Favorites logic)
  const getCatalogDiscountPct = () => {
    if (!catalogDiscountsForWholesaler.length || !product) return 0;
    let totalPct = 0;
    catalogDiscountsForWholesaler.forEach(d => {
      if (d.type === 'flat') {
        totalPct += d.percentage || 0;
      } else if (d.type === 'targeted') {
        const matches =
          (d.targetType === 'category' && (product.category || '').toLowerCase() === (d.targetValue || '').toLowerCase()) ||
          (d.targetType === 'brand' && (product.brand || '').toLowerCase() === (d.targetValue || '').toLowerCase());
        if (matches) totalPct += d.targetPercentage || 0;
      }
    });
    return Math.min(totalPct, 90);
  };

  const computePricing = () => {
    if (!product) return { originalPrice: 0, effectivePrice: 0, hasAnySaving: false, priceColor: 'text-green-600', showFlashBadge: false };
    const originalPrice = product.pricing?.unitPrice || product.pricing?.basePrice || 0;
    const catPct = getCatalogDiscountPct();
    const cdPct = customerDiscountPct;
    let effectivePrice = originalPrice;
    let priceColor = 'text-green-600';
    let showFlashBadge = false;
    if (flashOffer) {
      // Flash offer = final price, no other discounts on top
      effectivePrice = flashOffer.offerPrice;
      priceColor = 'text-red-600';
      showFlashBadge = true;
    } else if (productDiscount) {
      const effectivePct = Math.min(Math.max(productDiscount.percentage, cdPct) + catPct, 100);
      effectivePrice = Math.round(originalPrice * (1 - effectivePct / 100) * 100) / 100;
      priceColor = 'text-purple-700';
    } else if (catPct > 0) {
      const totalPct = Math.min(catPct + cdPct, 100);
      effectivePrice = Math.round(originalPrice * (1 - totalPct / 100) * 100) / 100;
      priceColor = 'text-teal-700';
    } else if (cdPct > 0) {
      effectivePrice = Math.round(originalPrice * (1 - cdPct / 100) * 100) / 100;
      priceColor = 'text-emerald-600';
    }
    return { originalPrice, effectivePrice, hasAnySaving: effectivePrice < originalPrice, priceColor, showFlashBadge };
  };

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      toast.error(t('productDetails.loginRequired'));
      return;
    }

    if (user?.role !== 'business') {
      toast.error(t('productDetails.loginRequired'));
      return;
    }

    addToCart({
      ...product,
      ...(flashOffer && {
        flashOfferPrice: flashOffer.offerPrice,
        flashOfferId: flashOffer.offerId,
        flashOfferTitle: flashOffer.offerTitle
      })
    }, quantity);
    toast.success(t('productDetails.addedToCart'));
  };

  // Helper function to get stock status badge
  const getStockStatusBadge = () => {
    const stockStatus = product.stockStatus;

    if (stockStatus === 'in_stock') {
      return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">{t('productDetails.inStock')}</span>;
    } else if (stockStatus === 'low_stock') {
      return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">{t('productDetails.lowStock')}</span>;
    } else if (stockStatus === 'out_of_stock') {
      return <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">{t('productDetails.outOfStock')}</span>;
    }

    // Fallback for legacy data
    if (product.availability?.inStock === true) {
      return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">{t('productDetails.inStock')}</span>;
    } else if (product.availability?.inStock === false) {
      return <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">{t('productDetails.outOfStock')}</span>;
    }

    return null;
  };

  if (loading) return <div className="min-h-screen flex justify-center items-center"><LoadingSpinner size="lg" /></div>;

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-16 lg:pt-0">
          <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <Package className="w-8 h-8 text-gray-400" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                {t('productDetails.notFound')}
              </h1>
              <p className="text-gray-600 mb-6">
                {t('productDetails.notFoundHint', 'This product is no longer available.')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center justify-center px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  {t('productDetails.goBack', 'Go back')}
                </button>
                <button
                  onClick={() => navigate('/products')}
                  className="inline-flex items-center justify-center px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  {t('productDetails.backToCatalog')}
                </button>
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              <span className="font-medium">{t('productDetails.backToCatalog')}</span>
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
              {/* Product Image Section */}
              <div className="space-y-4">
                <div className="bg-gray-100 rounded-lg overflow-hidden relative">
                  {product.images && product.images.length > 0 ? (
                    <img
                      // ✅ FIX: Use getImageUrl for proper Cloudinary/Local handling
                      src={getImageUrl(product.images[0])}
                      alt={product.name}
                      className="w-full h-48 lg:h-80 object-contain bg-white"
                      // ✅ FIX: Prevent infinite flicker loop
                      onError={(e) => {
                        if (!e.target.dataset.errorHandled) {
                          e.target.dataset.errorHandled = 'true';
                          e.target.style.display = 'none'; // Hide broken image
                          e.target.parentNode.classList.add('flex', 'items-center', 'justify-center');
                          e.target.parentNode.innerHTML = '<div class="text-gray-400 flex flex-col items-center"><svg class="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg><span>Image not available</span></div>';
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-48 lg:h-80 flex items-center justify-center bg-gray-200">
                      <Package className="w-20 h-20 text-gray-400" />
                    </div>
                  )}
                  
                  <div className="absolute top-4 right-4 z-10">
                    <FavoriteButton productId={product._id} size="lg" />
                  </div>
                </div>
              </div>

              {/* Product Info Section */}
              <div className="space-y-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-3">{product.name}</h1>
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium capitalize">
                      {getCategoryLabel(product.category)}
                    </span>
                    {getStockStatusBadge()}
                  </div>
                  <div className="mb-4">
                    {hidePricing ? (
                      <span className="text-xl text-gray-400 italic">{t('catalog.priceOnAgreement', 'Price on agreement')}</span>
                    ) : (() => {
                      const { originalPrice, effectivePrice, hasAnySaving, priceColor, showFlashBadge } = computePricing();
                      const unitLabel = t(`products:units.${product.pricing?.unit}`, product.pricing?.unit);
                      if (hasAnySaving) {
                        const pct = Math.round(((originalPrice - effectivePrice) / originalPrice) * 100);
                        return (
                          <div className="flex items-baseline flex-wrap gap-2">
                            <span className={`text-3xl font-bold ${priceColor}`}>€{effectivePrice.toFixed(2)}</span>
                            <span className="text-gray-400 line-through text-lg">€{originalPrice.toFixed(2)}</span>
                            <span className="text-gray-500 text-base">/ {unitLabel}</span>
                            {showFlashBadge ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-md shadow-sm">
                                ⚡ -{pct}% FLASH
                              </span>
                            ) : (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold text-white rounded-md shadow-sm ${productDiscount ? 'bg-purple-600' : getCatalogDiscountPct() > 0 ? 'bg-teal-600' : 'bg-emerald-600'}`}>
                                <Tag className="w-3 h-3" />
                                -{pct}%
                              </span>
                            )}
                          </div>
                        );
                      }
                      return (
                        <>
                          <span className="text-3xl font-bold text-green-600">€{originalPrice.toFixed(2)}</span>
                          <span className="text-gray-500 ml-2 text-base">/ {unitLabel}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('productDetails.description')}</h2>
                  <p className="text-gray-600 leading-relaxed">{product.description}</p>
                </div>

                {product.specifications && Object.keys(product.specifications).length > 0 && (
                  <div className="border-t pt-4">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('productDetails.specifications')}</h2>
                    <div className="space-y-2">
                      {Object.entries(product.specifications).map(([key, value]) => (
                        value && (
                          <div key={key} className="flex justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-600 capitalize font-medium">{t(`productDetails.${key}`, key.replace(/_/g, ' '))}:</span>
                            <span className="text-gray-900 font-semibold">{value}</span>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t pt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {product.brand && (
                      <div><span className="font-semibold text-gray-700">{t('productDetails.brand')}:</span> <p className="text-gray-600 mt-1">{product.brand}</p></div>
                    )}
                    <div><span className="font-semibold text-gray-700">SKU:</span> <p className="text-gray-600 mt-1">{product.sku}</p></div>
                    <div><span className="font-semibold text-gray-700">{t('productDetails.unit')}:</span> <p className="text-gray-600 mt-1 capitalize">{t(`products:units.${product.pricing?.unit}`, product.pricing?.unit)}</p></div>
                    {!hidePricing && product.pricing?.bulkPrice && (
                      <div><span className="font-semibold text-gray-700">{t('productDetails.price')}:</span> <p className="text-gray-600 mt-1">€{product.pricing.bulkPrice.toFixed(2)}</p></div>
                    )}
                  </div>
                </div>

                {isAuthenticated && user?.role === 'business' && (
                  <div className="border-t pt-6">
                    {product.stockStatus !== 'out_of_stock' && (product.availability?.inStock || product.stockStatus === 'in_stock' || product.stockStatus === 'low_stock') ? (
                      <div className="space-y-4">
                        {product.stockStatus === 'low_stock' && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-yellow-800 text-sm font-medium">⚠️ {t('productDetails.limitedStock')}</p>
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('productDetails.quantity')} ({product.pricing?.unit})
                          </label>
                          <div className="flex items-center space-x-4">
                            {(() => { const step = product.pricing?.minOrderQuantity || 1; return (<>
                            <button onClick={() => setQuantity(Math.max(step, quantity - step))} className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors text-lg font-semibold">−</button>
                            <input type="number" min={step} step={step} value={quantity} onChange={(e) => setQuantity(Math.max(step, parseInt(e.target.value) || step))} className="w-20 text-center text-lg font-semibold border border-gray-300 rounded-lg p-2" />
                            <button onClick={() => setQuantity(quantity + step)} className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors text-lg font-semibold">+</button>
                            </>); })()}
                          </div>
                        </div>
                        {product.pricing?.minOrderQuantity && (
                          <p className="text-sm text-gray-600">{t('productDetails.minOrder')}: {product.pricing.minOrderQuantity} {product.pricing.unit}(s)</p>
                        )}
                        <button onClick={handleAddToCart} className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-md hover:shadow-lg">
                          {hidePricing ? t('productDetails.addToCart') : `${t('productDetails.addToCart')} - €${(computePricing().effectivePrice * quantity).toFixed(2)}`}
                        </button>
                      </div>
                    ) : (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                        <p className="text-red-800 font-medium">{t('productDetails.outOfStock')}</p>
                      </div>
                    )}
                  </div>
                )}

                {!isAuthenticated && (
                  <div className="border-t pt-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                      <p className="text-blue-800 font-medium mb-3">{t('productDetails.loginRequired')}</p>
                      <button onClick={() => navigate('/login')} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">{t('common.login')}</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default ProductDetails;